/**
 * 路由守卫:
 *   ProtectedRoute   需登录
 *   AdminRoute       需 company_admin 或 super_admin
 *   SuperAdminRoute  仅 super_admin
 *   CompanyAdminRoute 仅 company_admin
 * 替代旧版 auth-guard.js 的全局 fetch 拦截 + redirect 逻辑。
 *
 * 普通用户 (role=user) 的默认前台是旧版 /static/ (经典版): 直接打开 SPA 根 /admin/
 * (首次加载落在 "/", 不是在应用内点「报价」tab 过来的) 时整页跳过去。
 * 深链 (/admin/image、/admin/history …) 与应用内导航不受影响。
 */
import { useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Spinner } from "@/components/ui";
import { goLegacyHome, prefersLegacyHome } from "@/features/auth/utils";
import { useAuthStore } from "@/stores/auth-store";

/**
 * 整页跳到旧版前台; 跳转前显示一个加载态。
 * 默认 replace (后退不会再被弹回来); 登录 / 注册页传 replace={false} (同 window.location.assign)。
 */
export function LegacyHomeRedirect({ replace = true }: { replace?: boolean }) {
  useEffect(() => {
    goLegacyHome(replace);
  }, [replace]);
  return (
    <div className="flex h-[100dvh] items-center justify-center gap-2 text-sm text-muted-foreground">
      <Spinner /> 正在进入经典版…
    </div>
  );
}

export function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const token = useAuthStore((s) => s.token);
  const role = useAuthStore((s) => s.user?.role);
  const location = useLocation();
  if (!token) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  // key === "default": 本次页面加载的第一个位置 (地址栏直接打开 / 外部链接), 而非应用内跳转
  if (prefersLegacyHome(role) && location.pathname === "/" && location.key === "default") {
    return <LegacyHomeRedirect />;
  }
  return <>{children}</>;
}

export function AdminRoute({ children }: { children: React.ReactNode }) {
  const isAdmin = useAuthStore((s) => s.isAdmin)();
  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

/** 仅 super_admin。company_admin 直接敲 /admin/companies 时踢回 admin 首页,
 *  而不是让页面去请求一个必定 403 的接口。 */
export function SuperAdminRoute({ children }: { children: React.ReactNode }) {
  const role = useAuthStore((s) => s.user?.role);
  if (role !== "super_admin") {
    return <Navigate to="/admin/users" replace />;
  }
  return <>{children}</>;
}

/** 仅 company_admin (余额 / 邀请都按公司算, super_admin 没有所属公司) */
export function CompanyAdminRoute({ children }: { children: React.ReactNode }) {
  const role = useAuthStore((s) => s.user?.role);
  if (role !== "company_admin") {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}
